import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycPartyCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { PartyDto, PartyPayload } from "@/features/kyc/api/dto";

/**
 * Crée l'identité métier d'un client (`POST /Parties/add`) — étape 2 du
 * parcours d'inscription. Idempotency-Key OBLIGATOIRE (guide §4.7) :
 * clé persistée par empreinte du corps ; 409 = attendre puis rejouer ;
 * 422 = nouvelle tentative.
 */
export const useCreateParty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "parties", "create"],
    mutationFn: (body: PartyPayload) =>
      mutateIdempotent<PartyDto>({
        scopeId: "kyc.parties.add",
        url: kycEndpoints.parties.add,
        body,
      }),
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, kycPartyCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateParty failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
